/* eslint-disable max-len */
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  capitalize,
  useTheme,
} from '@mui/material';
import { ACTION_CONFIRMATION_VIEW } from 'pages/Dashboard/enums';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { WaypointSuggestion } from 'pages/Dashboard/types/waypointSuggestion.types';
import React, { useCallback, useMemo, useState } from 'react';
import { Waypoints } from 'pages/Dashboard/types';
import HLCard from 'components/HLCard';
import {
  CheckCircle,
  Bedtime,
  Coffee,
  Fastfood,
  Female,
  FitnessCenter,
  Forest,
  LiveTv,
  LocationOn,
  People,
  SmokingRooms,
  ThumbUp,
  WbSunny,
  WineBar,
} from '@mui/icons-material';
import { useHttp } from 'hooks/use-fetch';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import HLButton from 'components/HLButton';

enum TableCategories {
  Substance = 'substance',
  Digital = 'digital',
  Behavior = 'behavior',
  Other = 'other',
  Custom = 'custom',
}

interface SendWaypointSuggestionsProps {
  patient: PatientObj;
  standardWaypoints: Waypoints[];
  patientWaypoints: Waypoints[];
  handleCloseDrawer: () => void;
  handleSendSuggestions: (suggestions: Partial<WaypointSuggestion>[]) => Promise<void>;
}

enum WaypointIconNames {
  Social = 'social',
  Diet = 'diet',
  Exercise = 'exercise',
  Sleep = 'sleep',
  Alcohol = 'alcohol',
  TV = 'television',
  Marijuana = 'marijuana',
  Caffeine = 'caffeine',
  Nicotine = 'nicotine',
  SocialMedia = 'socialmedia',
  Meditation = 'meditation',
  Libido = 'libido',
  Custom = 'Custom',
}

const tableCategoriesOrder = [
  TableCategories.Behavior,
  TableCategories.Substance,
  TableCategories.Digital,
  TableCategories.Other,
  TableCategories.Custom,
];

const getWaypointsIcon = (waypointName: string, color: string) => {
  switch (waypointName) {
    case WaypointIconNames.Social:
      return <People fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Diet:
      return <Fastfood fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Exercise:
      return <FitnessCenter fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Sleep:
      return <Bedtime fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Alcohol:
      return <WineBar fontSize='small' sx={{ color }} />;
    case WaypointIconNames.TV:
      return <LiveTv fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Caffeine:
      return <Coffee fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Marijuana:
      return <Forest fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Libido:
      return <Female fontSize='small' sx={{ color }} />;
    case WaypointIconNames.SocialMedia:
      return <ThumbUp fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Meditation:
      return <WbSunny fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Nicotine:
      return <SmokingRooms fontSize='small' sx={{ color }} />;
    case WaypointIconNames.Custom:
      return <LocationOn fontSize='small' sx={{ color }} />;
  }
  return null;
};

export default function SendWaypointSuggestions({
  patient,
  standardWaypoints,
  patientWaypoints,
  handleCloseDrawer,
  handleSendSuggestions,
}: SendWaypointSuggestionsProps) {
  const { isNewVersionEnabled } = useHttp();
  const [view, setView] = useState(ACTION_CONFIRMATION_VIEW.DEFAULT);
  const theme = useTheme();

  const [suggestions, setSuggestions] = useState<WaypointSuggestion[]>([]);

  const [customWaypointName, setCustomWaypointName] = useState('');
  const [customWaypointError, setCustomWaypointError] = useState('');
  const [showCustomWaypointForm, setShowCustomWaypointForm] = useState(false);
  const [sendingSuggestions, setSendingSuggestions] = useState(false);

  const [comment, setComment] = useState('');

  const patientName = patient?.name?.firstName && patient?.name?.firstName?.length > 0
    ? capitalize(patient?.name?.firstName ?? '')
    : 'the patient';

  const mergedWaypoints = [
    ...standardWaypoints,
    ...patientWaypoints.filter((pw) => !standardWaypoints.some((sw) => sw.id === pw.id)),
    // Add suggestions that are not in patientWaypoints or standardWaypoints
    ...suggestions.filter((suggestion) => !patientWaypoints.some((pw) => pw.id === suggestion.id)),
  ];
  const uniqueWaypoints = mergedWaypoints.filter(
    (waypoint, index, array) => array.findIndex((w) => w.id === waypoint.id) === index,
  );

  // Loop through tableCategoriesOrder and group uniqueWaypoints by category
  const groupedWaypoints = tableCategoriesOrder.reduce<{ [key: string]: Waypoints[] }>(
    (acc, category) => {
      acc[category] = uniqueWaypoints.filter((waypoint) => waypoint.category.toLowerCase() === category.toLowerCase());
      return acc;
    },
    {},
  );

  const handleCheckboxChange = (waypoint: Waypoints, isChecked: boolean) => {
    const isSuggested = suggestions.find((sw) => sw.id === waypoint.id);
    let newSuggestedWaypoints;

    if (isChecked && !isSuggested) {
      // Add to suggestions with type ADD
      newSuggestedWaypoints = [
        ...suggestions,
        {
          id: waypoint.id,
          name: waypoint.name,
          type: waypoint.type,
          displayName: waypoint.displayName || waypoint.name,
          category: waypoint.category,
          comment: '', // You might want to handle comments differently
          suggestionType: 'ADD',
        },
      ];
    }
    else if (!isChecked && isSuggested && isSuggested.suggestionType === 'ADD') {
      // Remove from suggestions if it was added before
      newSuggestedWaypoints = suggestions.filter((sw) => sw.id !== waypoint.id);
    }
    else if (isChecked && isSuggested) {
      // Checkbox is checked and waypoint is already suggested, remove from suggestions
      newSuggestedWaypoints = suggestions.filter((sw) => sw.id !== waypoint.id);
    }
    else if (!isChecked && !isSuggested) {
      // If unchecking a checkbox not in suggestions, add with REMOVE type
      newSuggestedWaypoints = [
        ...suggestions,
        {
          id: waypoint.id,
          name: waypoint.name,
          type: waypoint?.type,
          displayName: waypoint.displayName || waypoint.name,
          category: waypoint.category,
          comment: '', // Handle comments as needed
          suggestionType: 'REMOVE',
        },
      ];
    }
    else {
      // Fallback case, typically should not reach here
      newSuggestedWaypoints = [...suggestions];
    }

    setSuggestions(newSuggestedWaypoints);
  };

  const handleSendSuggestionClick = () => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_SEND_SUGGESTIONS_BUTTON_CLICKED, {
      location: 'progress_monitor_tab_edit_activities_drawer',
    });
    setView(ACTION_CONFIRMATION_VIEW.CONFIRMATION);
  };

  const handleAddCustomWaypoint = () => {
    if (!customWaypointName) {
      setCustomWaypointError('Custom waypoint label is required');
      return;
    }
    const isDuplicate = uniqueWaypoints.find(
      (w) => w.name.toLowerCase() === customWaypointName.toLowerCase(),
    );
    if (isDuplicate) {
      setCustomWaypointError('Waypoint already exists');
      return;
    }
    setSuggestions([
      ...suggestions,
      {
        name: customWaypointName.toLowerCase(),
        displayName: customWaypointName,
        category: 'Custom',
        comment: '',
        suggestionType: 'ADD',
        type: 'WAYPOINT_TYPE_PROVIDER_CREATED',
        // generate a random uuid for the id
        id: Math.random().toString(36).substring(7),
      },
    ]);
    setCustomWaypointName('');
    setShowCustomWaypointForm(false);
  };

  const handleConfirmSendSuggestions = async () => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_SEND_SUGGESTIONS_CONFIRMATION_BUTTON_CLICKED, {
      'waypoints to add': suggestions
        .filter((s) => s.suggestionType === 'ADD')
        .map((s) => s.name)
        .join(', '),
      'waypoints to remove': suggestions
        .filter((s) => s.suggestionType === 'REMOVE')
        .map((s) => s.name)
        .join(', '),
      location: 'progress_monitor_tab_edit_activities_drawer',
    });
    setSendingSuggestions(true);
    await handleSendSuggestions(
      suggestions.map((suggestion) => ({
        id: suggestion.id,
        type: suggestion.type,
        name: suggestion.name,
        displayName: suggestion.displayName,
        category: suggestion.category,
        suggestionType: suggestion.suggestionType,
        comment,
      })),
    );
    setSendingSuggestions(false);
    setView(ACTION_CONFIRMATION_VIEW.SUCCESS);
  };

  const handleCancelSendSuggestions = useCallback(() => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_SEND_SUGGESTIONS_CANCEL_BUTTON_CLICKED, {
      location: 'progress_monitor_tab_edit_activities_drawer',
    });
    setView(ACTION_CONFIRMATION_VIEW.DEFAULT);
  }, []);

  const newWaypoints = useMemo(() => {
    let newWaypointsCount = patientWaypoints.length;
    suggestions.forEach((suggestion) => {
      if (suggestion.suggestionType === 'ADD') {
        newWaypointsCount += 1;
      }
      else if (suggestion.suggestionType === 'REMOVE') {
        newWaypointsCount -= 1;
      }
    });
    return newWaypointsCount;
  }, [patientWaypoints, suggestions]);

  const showMinimumWaypointsAlert = newWaypoints < 3 && !!suggestions.length && !!patientWaypoints.length;

  const activityText = isNewVersionEnabled ? 'Activity' : 'Waypoint';
  const activitiesText = isNewVersionEnabled ? 'activities' : 'waypoints';

  return (
    <Box sx={{ width: '100%' }}>
      {view === ACTION_CONFIRMATION_VIEW.DEFAULT && (
        <>
          <Typography variant='body2' color='text.primary' sx={{ mb: 2 }} className='fs-mask'>
            {`Suggest ${activitiesText} to ${patientName} to ${patientWaypoints.length ? 'start/stop' : 'start'} tracking.`}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {tableCategoriesOrder.map((category) => {
              const waypoints = groupedWaypoints[category] || [];
              if (!waypoints.length && category !== TableCategories.Custom) return null;
              const isCustom = category.toLowerCase() === 'custom';
              return (
                <Grid item xs={12} key={category} sx={{ mb: 0 }}>
                  <HLCard sx={{ p: 1.5, mb: 0, position: isCustom ? 'relative' : undefined }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between'>
                      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1, textTransform: 'capitalize' }}>
                        {category === TableCategories.Behavior ? 'Behaviour' : category.charAt(0).toUpperCase() + category.slice(1)}
                      </Typography>
                      {isCustom && !showCustomWaypointForm && (
                        <HLButton
                          variant='text'
                          sx={{ minWidth: 120, fontWeight: 500 }}
                          onClick={() => setShowCustomWaypointForm(true)}
                          disabled={patient?.demoPatient}
                        >
                          + Add Custom Activity
                        </HLButton>
                      )}
                    </Box>
                    <Box display='flex' flexWrap='wrap' gap={2}>
                      {waypoints.map((categoryPatientWaypoint) => {
                        const displayName = categoryPatientWaypoint.displayName ?? categoryPatientWaypoint.name;
                        const suggestedWaypoint = suggestions.find((sw) => sw.id === categoryPatientWaypoint.id);
                        const isChecked = suggestedWaypoint
                          ? suggestedWaypoint.suggestionType === 'ADD'
                          : patientWaypoints.some((pw) => pw.id === categoryPatientWaypoint.id);
                        return (
                          <Box
                            key={`${categoryPatientWaypoint.id}-${isChecked ? '-checked' : ''}`}
                            sx={{
                              border: suggestedWaypoint ? `1px dashed ${theme.palette.primary.main}` : 'none',
                              backgroundColor: suggestedWaypoint ? `${theme.palette.primary.main}0D` : 'transparent',
                              fontStyle: suggestedWaypoint ? 'italic' : 'normal',
                              textDecoration: suggestedWaypoint && !isChecked ? 'line-through' : 'none',
                              borderRadius: '4px',
                              px: 1,
                              py: 0.5,
                              minWidth: 120,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size='small'
                                  checked={isChecked}
                                  onChange={() => handleCheckboxChange(categoryPatientWaypoint, !isChecked)}
                                />
                              )}
                              label={(
                                <Typography variant='body2' color='text.primary'>
                                  {displayName}
                                </Typography>
                              )}
                              sx={{ pl: 1, mr: 0, fontSize: theme.typography.body2.fontSize }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Custom Waypoint Form and comment input inside card */}
                    {isCustom && (
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {showCustomWaypointForm && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <TextField
                              label={`Custom ${activityText}`}
                              variant='outlined'
                              placeholder={`${activityText} Name`}
                              size='small'
                              sx={{ width: 'auto', minWidth: 200 }}
                              value={customWaypointName}
                              onChange={(e) => {
                                if (e.target.value.length > 0) setCustomWaypointError('');
                                setCustomWaypointName(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddCustomWaypoint();
                                }
                              }}
                              autoFocus
                              error={!!customWaypointError}
                            />
                            <HLButton variant='contained' onClick={handleAddCustomWaypoint} sx={{ minWidth: 80 }}>
                              Add
                            </HLButton>
                            <HLButton
                              variant='outlined'
                              onClick={() => {
                                setShowCustomWaypointForm(false);
                                setCustomWaypointName('');
                              }}
                              sx={{ minWidth: 80 }}
                            >
                              Cancel
                            </HLButton>
                            <Typography variant='caption' color='error'>
                              {customWaypointError}
                            </Typography>
                          </Box>
                        )}
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                          {`${patientName} will be notified in their Headlamp mobile app.`}
                        </Typography>
                        <TextField
                          label='Comment to patient (Optional)'
                          variant='outlined'
                          placeholder='Comment to patient (Optional)'
                          size='small'
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          sx={{ mb: 2, width: '100%' }}
                        />
                        {showMinimumWaypointsAlert ? (
                          <Alert severity='error' sx={{ mb: 2 }}>
                            The patient must have at least 3
                            <br />
                            {activitiesText}
                            .
                          </Alert>
                        ) : null}
                      </Box>
                    )}
                  </HLCard>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <HLButton
              variant='contained'
              sx={{ width: 180, mt: 2 }}
              onClick={handleSendSuggestionClick}
              disabled={suggestions.length === 0 || showMinimumWaypointsAlert || patient?.demoPatient}
            >
              Send Suggestions
            </HLButton>
          </Box>
        </>
      )}
      {view === ACTION_CONFIRMATION_VIEW.CONFIRMATION && (
        <HLCard sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='h5' fontWeight={400} color='text.primary'>
            Please confirm
          </Typography>
          <Typography variant='h6' fontWeight={500} color='text.primary'>
            Send these
            {' '}
            {activityText.toLowerCase()}
            {' '}
            suggestions?
          </Typography>
          <Box sx={{ width: '100%', mb: 2 }}>
            {suggestions.map((suggestion) => (
              <Box display='flex' gap={1} key={suggestion.id}>
                {getWaypointsIcon(
                  suggestion.category.toLowerCase() === 'custom'
                    ? WaypointIconNames.Custom
                    : suggestion.name.toLowerCase(),
                  theme.palette.text.secondary,
                )}
                <Typography variant='body2' color='text.secondary'>
                  {`${suggestion.displayName} ${suggestion.suggestionType === 'ADD' ? '(add)' : '(remove)'}`}
                </Typography>
              </Box>
            ))}
          </Box>
          {comment && (
            <Typography variant='body1' className='fs-mask' color='text.primary'>
              {`Comment to ${patientName}:`}
              <br />
              <Typography component='i' variant='body1' className='fs-mask' color='text.secondary'>
                {comment}
              </Typography>
            </Typography>
          )}
          <Box display='flex' gap={2}>
            <HLButton
              variant='contained'
              onClick={handleConfirmSendSuggestions}
              disabled={sendingSuggestions}
            >
              Yes, Send
              {sendingSuggestions && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </HLButton>
            <HLButton variant='outlined' onClick={handleCancelSendSuggestions}>
              Cancel
            </HLButton>
          </Box>
        </HLCard>
      )}
      {view === ACTION_CONFIRMATION_VIEW.SUCCESS && (
        <HLCard sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box display='flex' alignItems='center' gap={1}>
            <CheckCircle fontSize='large' color='success' />
            <Typography variant='h5' sx={{ mb: 0 }} fontWeight={400} color='text.primary'>
              Success!
            </Typography>
          </Box>
          <Typography variant='h6' fontWeight={500} color='text.primary'>
            {activityText}
            {' '}
            suggestions have been sent.
          </Typography>
          <HLButton variant='contained' onClick={handleCloseDrawer}>
            Close
          </HLButton>
        </HLCard>
      )}
    </Box>
  );
}
